import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom'
import {
  deleteFile,
  distillFile,
  downloadFile,
  getActiveJob,
  getJobStatus,
  getProjectFiles,
  getProjectTags,
  indexProject,
  reindexFile,
  reindexProject,
  replaceFile,
  stopProjectIndex,
  type TagSuggestion,
} from '../api/files'
import { type MemoryItem, memoryApi } from '../api/memory'
import {
  type AgentSession,
  type MemoryTimelineItem,
  memoryV2Api,
  type SessionHandoff,
  type V2MemorySummary,
  type VerifyMemoryResult,
} from '../api/memory-v2'
import {
  deleteProjectIcon,
  getAuthenticatedIconUrl,
  getProject,
  previewProjectCleanup,
  relinkProjectEmbeddingProvider,
  runProjectCleanup,
  updateProject,
  uploadProjectIcon,
} from '../api/projects'
import { listEmbeddingProviders } from '../api/providers'
import { type AnswerChunk, parseFilters, searchProject, streamAnswer } from '../api/search'
import { getSettings } from '../api/settings'
import { getProjectTokenSavings } from '../api/token-savings'
import Button from '../components/Button'
import { AddConnectorModal } from '../components/connectors/AddConnectorModal'
import { ConnectorDetail } from '../components/connectors/ConnectorDetail'
import { ConnectorList } from '../components/connectors/ConnectorList'
import DocTypeChipFilter from '../components/DocTypeChipFilter'
import EnrichmentStatusTag from '../components/EnrichmentStatusTag'
import FileTypeIcon from '../components/FileTypeIcon'
import IndexingStepLine from '../components/IndexingStepLine'
import MarkdownPreviewModal from '../components/MarkdownPreviewModal'
import Modal from '../components/Modal'
import { HandoffDetails } from '../components/memory/HandoffDetails'
import { JobHealth } from '../components/memory/JobHealth'
import { MemoryComposer } from '../components/memory/MemoryComposer'
import { MemoryList } from '../components/memory/MemoryList'
import { ProvenanceChain } from '../components/memory/ProvenanceChain'
import { SessionHistory } from '../components/memory/SessionHistory'
import { ProjectPromptsTab } from '../components/prompts/ProjectPromptsTab'
import SearchFiltersPanel from '../components/SearchFilters'
import StatusBadge from '../components/StatusBadge'
import TranscriptDistillModal from '../components/TranscriptDistillModal'
import ProjectTokenSavingsPanel from '../components/token-savings/ProjectTokenSavingsPanel'
import { useAuth } from '../hooks/useAuth'
import type {
  ConnectorResponse,
  EmbeddingProvider,
  EmbeddingProviderEntry,
  FileResponse,
  IndexJobLiveStatus,
  ProjectCleanupOptionId,
  ProjectCleanupPreview,
  ProjectDetailResponse,
  SearchResultItem,
  SettingsResponse,
} from '../types/api'
import { DOC_TYPES, type DocType, toDocTypes } from '../utils/docTypes'
import { formatFileSize, formatMatchPercent } from '../utils/format'
import { canManageProject, isGlobalAdmin, projectRoleLabel } from '../utils/rbac'
import { AreaDrilldownPanel } from './knowledge/AreaDrilldownPanel'
import { CodeGraphTab } from './knowledge/CodeGraphTab'
import type { KnowledgeArea } from './knowledge/knowledgeTypes'
import { ModulesTab } from './knowledge/ModulesTab'

const PROVIDER_OPTIONS: Array<{ value: EmbeddingProvider; label: string }> = [
  { value: 'local', label: 'Local (Transformers.js)' },
  { value: 'lm_studio', label: 'LM Studio' },
  { value: 'nvidia_nim', label: 'NVIDIA NIM' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'openai_compatible', label: 'OpenAI-compatible' },
]

const inputClassName =
  'w-full px-4 py-3 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent'

function providerLabel(provider: EmbeddingProvider): string {
  return PROVIDER_OPTIONS.find((o) => o.value === provider)?.label ?? provider
}

function isFileSelectable(file: FileResponse, isIndexing: boolean): boolean {
  return !(file.indexingStatus === 'indexing' && isIndexing)
}

function formatElapsed(ms: number): string {
  const sec = Math.floor(ms / 1000)
  const m = Math.floor(sec / 60)
  const s = sec % 60
  if (m <= 0) return `${s}s`
  return `${m}m ${s}s`
}

function formatEta(seconds: number | null): string {
  if (seconds === null) return '—'
  if (seconds < 60) return `~${seconds}s`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return s > 0 ? `~${m}m ${s}s` : `~${m}m`
}

const STALE_HEARTBEAT_MS = 60_000

const VALID_CONTENT_TABS = [
  'files',
  'connectors',
  'memories',
  'prompts',
  'code',
  'modules',
] as const
type ContentTab = (typeof VALID_CONTENT_TABS)[number]
function isContentTab(value: string | null): value is ContentTab {
  return VALID_CONTENT_TABS.includes(value as ContentTab)
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return value
  }
}

export default function ProjectDetailPage() {
  const { user } = useAuth()
  const { id } = useParams<{ id: string }>()
  const projectId = Number(id)
  const location = useLocation()
  const highlightIndex =
    (location.state as { highlightIndex?: boolean } | null)?.highlightIndex ?? false

  const [project, setProject] = useState<ProjectDetailResponse | null>(null)
  const [files, setFiles] = useState<FileResponse[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isIndexing, setIsIndexing] = useState(false)
  const [indexingJob, setIndexingJob] = useState<IndexJobLiveStatus | null>(null)
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastFilesProcessedRef = useRef(0)
  const lastFileRefreshRef = useRef(0)
  const lastPhaseRef = useRef<string | null>(null)
  const lastCurrentFileRef = useRef<number | null>(null)
  const [replacingFileId, setReplacingFileId] = useState<number | null>(null)
  const replaceInputRef = useRef<HTMLInputElement>(null)
  const [replaceTarget, setReplaceTarget] = useState<FileResponse | null>(null)
  const [reindexModal, setReindexModal] = useState<{
    isOpen: boolean
    fileId: number | null
    fileName: string
    afterReplace?: boolean
  }>({ isOpen: false, fileId: null, fileName: '' })
  const [reindexAllModal, setReindexAllModal] = useState(false)
  const [stopConfirmOpen, setStopConfirmOpen] = useState(false)
  const [isStopping, setIsStopping] = useState(false)
  const [embeddingModal, setEmbeddingModal] = useState(false)
  const [embeddingConfirm, setEmbeddingConfirm] = useState(false)
  const [isSavingEmbedding, setIsSavingEmbedding] = useState(false)
  const [isSavingDescription, setIsSavingDescription] = useState(false)
  const [editDescription, setEditDescription] = useState('')
  const [editContextualEnrichment, setEditContextualEnrichment] = useState(false)
  const [isSavingEnrichmentToggle, setIsSavingEnrichmentToggle] = useState(false)
  const [editTranscriptDistillation, setEditTranscriptDistillation] = useState(false)
  const [isSavingDistillationToggle, setIsSavingDistillationToggle] = useState(false)
  const [isUploadingIcon, setIsUploadingIcon] = useState(false)
  const [failedIconUrl, setFailedIconUrl] = useState<string | null>(null)
  // Proxy tokens avoided in the last 24h, for the header stats grid — same
  // number the Token savings panel below shows, fetched separately since the
  // header renders above that panel. null while loading/unavailable/denied.
  const [headerProxyTokens, setHeaderProxyTokens] = useState<number | null>(null)
  const iconInputRef = useRef<HTMLInputElement>(null)
  const [accountSettings, setAccountSettings] = useState<SettingsResponse | null>(null)
  const [systemEmbeddingProviders, setSystemEmbeddingProviders] = useState<
    EmbeddingProviderEntry[]
  >([])
  const [selectedEmbeddingProviderId, setSelectedEmbeddingProviderId] = useState<number | ''>('')
  const [previewFile, setPreviewFile] = useState<FileResponse | null>(null)
  const [distillFileId, setDistillFileId] = useState<number | null>(null)
  const [distillingFileId, setDistillingFileId] = useState<number | null>(null)
  const [distillJobId, setDistillJobId] = useState<number | null>(null)
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean
    mode: 'single' | 'bulk'
    fileId: number | null
    fileName: string
    count: number
  }>({ isOpen: false, mode: 'single', fileId: null, fileName: '', count: 0 })
  const [isDeleting, setIsDeleting] = useState(false)
  const [cleanupPreview, setCleanupPreview] = useState<ProjectCleanupPreview | null>(null)
  const [selectedCleanupOptions, setSelectedCleanupOptions] = useState<Set<ProjectCleanupOptionId>>(
    new Set()
  )
  const [isLoadingCleanup, setIsLoadingCleanup] = useState(false)
  const [isCleaning, setIsCleaning] = useState(false)
  const [selectedFileIds, setSelectedFileIds] = useState<Set<number>>(new Set())
  const [filesDocTypeFilter, setFilesDocTypeFilter] = useState<DocType[]>([])
  const [filesShowFailedOnly, setFilesShowFailedOnly] = useState(false)
  const selectAllRef = useRef<HTMLInputElement>(null)

  // Test RAG state
  const [testRagExpanded, setTestRagExpanded] = useState(false)
  const [testQuery, setTestQuery] = useState('')
  const [testLimit, setTestLimit] = useState(5)
  const [testResults, setTestResults] = useState<SearchResultItem[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [searchWarning, setSearchWarning] = useState<string | null>(null)
  const [activeResultTab, setActiveResultTab] = useState<'answer' | 'sources'>('sources')
  const [testDocTypes, setTestDocTypes] = useState<DocType[]>([])
  const [testTags, setTestTags] = useState<string[]>([])
  const [tagSuggestions, setTagSuggestions] = useState<TagSuggestion[]>([])
  const [isInferring, setIsInferring] = useState(false)
  const [inferredNote, setInferredNote] = useState<string | null>(null)
  const [filesSearchQuery, setFilesSearchQuery] = useState('')
  const [filesPage, setFilesPage] = useState(1)
  const [filesPageSize, setFilesPageSize] = useState(10)
  const [memoriesSearchQuery, setMemoriesSearchQuery] = useState('')
  const [memoriesPage, setMemoriesPage] = useState(1)
  const [memoriesPageSize, setMemoriesPageSize] = useState(10)

  // AI Answer state
  const [aiAnswer, setAiAnswer] = useState('')
  const [isGeneratingAnswer, setIsGeneratingAnswer] = useState(false)
  const [answerError, setAnswerError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Connectors state — tab is persisted in URL (?tab=) so refresh restores the same tab
  const [searchParams, setSearchParams] = useSearchParams()
  const rawTab = searchParams.get('tab')
  const contentTab: ContentTab = isContentTab(rawTab) ? rawTab : 'files'
  const setContentTab = useCallback(
    (tab: ContentTab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('tab', tab)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )
  const [knowledgeAreas, setKnowledgeAreas] = useState<KnowledgeArea[]>([])
  const rawAreaId = searchParams.get('areaId')
  const parsedAreaId = rawAreaId != null ? Number(rawAreaId) : null
  const selectedAreaId = parsedAreaId != null && Number.isFinite(parsedAreaId) ? parsedAreaId : null
  const setSelectedAreaId = useCallback(
    (id: number | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (id == null) next.delete('areaId')
          else next.set('areaId', String(id))
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )
  const [showAddConnector, setShowAddConnector] = useState(false)
  const [detailConnectorId, setDetailConnectorId] = useState<number | null>(null)
  const [memories, setMemories] = useState<MemoryItem[]>([])
  const [isLoadingMemories, setIsLoadingMemories] = useState(false)
  const [memoriesLoaded, setMemoriesLoaded] = useState(false)
  // Memory Engine v2 governance (Task 11): sessions, handoffs, provenance,
  // lifecycle, and degraded/index health — no replay viewer, no live stream.
  const [v2Sessions, setV2Sessions] = useState<AgentSession[]>([])
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null)
  const [v2Handoffs, setV2Handoffs] = useState<SessionHandoff[]>([])
  const [v2Verify, setV2Verify] = useState<VerifyMemoryResult | null>(null)
  const [v2Timeline, setV2Timeline] = useState<MemoryTimelineItem[]>([])
  const [v2Summaries, setV2Summaries] = useState<V2MemorySummary[]>([])
  const [v2Degraded, setV2Degraded] = useState(false)
  const [v2Warnings, setV2Warnings] = useState<string[]>([])
  const [governanceLoaded, setGovernanceLoaded] = useState(false)

  const fetchData = useCallback(async () => {
    try {
      const [projectData, filesData] = await Promise.all([
        getProject(projectId),
        getProjectFiles(projectId),
      ])
      setProject(projectData)
      setFiles(filesData)
    } catch {
      toast.error('Failed to load project')
    } finally {
      setIsLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Poll a running background distillation job until it reaches a terminal
  // state, then refresh so the proposal badge appears. The interval is always
  // torn down on unmount or when the tracked job changes — no leak.
  useEffect(() => {
    if (distillJobId == null) return
    let cancelled = false
    const finish = (failed: boolean) => {
      if (cancelled) return
      setDistillJobId(null)
      if (failed) toast.error('Distillation failed')
      void fetchData().finally(() => setDistillingFileId(null))
    }
    const tick = async () => {
      try {
        const job = await getJobStatus(distillJobId)
        if (cancelled) return
        if (job.status === 'completed' || job.status === 'cancelled') finish(false)
        else if (job.status === 'failed') finish(true)
      } catch {
        finish(false)
      }
    }
    const id = setInterval(tick, 2500)
    void tick()
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [distillJobId, fetchData])

  useEffect(() => {
    if (!Number.isFinite(projectId)) return
    getProjectTags(projectId)
      .then(setTagSuggestions)
      .catch(() => setTagSuggestions([]))
  }, [projectId])

  useEffect(() => {
    setSelectedFileIds((prev) => {
      const validIds = new Set(files.map((f) => f.id))
      const next = new Set([...prev].filter((id) => validIds.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [files])

  const fileDocTypes = useMemo((): DocType[] => {
    const present = new Set<string>()
    for (const file of files) {
      if (file.docType) present.add(file.docType)
    }
    return DOC_TYPES.filter((type) => present.has(type))
  }, [files])

  useEffect(() => {
    setFilesDocTypeFilter((prev) => prev.filter((type) => fileDocTypes.includes(type)))
  }, [fileDocTypes])

  // Backend already returns files sorted by createdAt desc, but sort defensively for
  // any direct setFiles paths and to keep the UI deterministic.
  const sortedFiles = useMemo(() => {
    return [...files].sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0
      if (ta !== tb) return tb - ta
      return b.id - a.id
    })
  }, [files])

  const displayedFiles = useMemo(() => {
    let result = sortedFiles
    if (filesShowFailedOnly) {
      result = result.filter((file) => file.indexingStatus === 'failed')
    }
    if (filesDocTypeFilter.length === 0) return result
    return result.filter(
      (file) =>
        file.docType &&
        (DOC_TYPES as readonly string[]).includes(file.docType) &&
        filesDocTypeFilter.includes(file.docType as DocType)
    )
  }, [sortedFiles, filesDocTypeFilter, filesShowFailedOnly])

  const displayedFilesWithSearch = useMemo(() => {
    if (filesSearchQuery.trim() === '') return displayedFiles
    const query = filesSearchQuery.trim().toLowerCase()
    return displayedFiles.filter(
      (file) =>
        file.originalFilename.toLowerCase().includes(query) ||
        file.docType?.toLowerCase().includes(query)
    )
  }, [displayedFiles, filesSearchQuery])

  const pagedFiles = useMemo(() => {
    const start = (filesPage - 1) * filesPageSize
    const end = start + filesPageSize
    return displayedFilesWithSearch.slice(start, end)
  }, [displayedFilesWithSearch, filesPage, filesPageSize])

  const displayedMemoriesWithSearch = useMemo(() => {
    if (memoriesSearchQuery.trim() === '') return memories
    const query = memoriesSearchQuery.trim().toLowerCase()
    return memories.filter((item) => item.memory.toLowerCase().includes(query))
  }, [memories, memoriesSearchQuery])

  const pagedMemories = useMemo(() => {
    const start = (memoriesPage - 1) * memoriesPageSize
    const end = start + memoriesPageSize
    return displayedMemoriesWithSearch.slice(start, end)
  }, [displayedMemoriesWithSearch, memoriesPage, memoriesPageSize])

  // Reset/clamp pagination when filtering changes, matching Files tab pattern.
  // Without this, a narrowed filter can leave the page beyond the last page,
  // showing an empty list. See ProjectDetailPage pagination review.
  // biome-ignore lint/correctness/useExhaustiveDependencies: filter changes intentionally reset pagination
  useEffect(() => {
    setFilesPage(1)
  }, [filesSearchQuery, filesDocTypeFilter, filesShowFailedOnly])
  // biome-ignore lint/correctness/useExhaustiveDependencies: search changes intentionally reset pagination
  useEffect(() => {
    setMemoriesPage(1)
  }, [memoriesSearchQuery])
  useEffect(() => {
    const maxFilesPage = Math.max(1, Math.ceil(displayedFilesWithSearch.length / filesPageSize))
    if (filesPage > maxFilesPage) setFilesPage(maxFilesPage)
  }, [displayedFilesWithSearch.length, filesPageSize, filesPage])
  useEffect(() => {
    const maxMemoriesPage = Math.max(
      1,
      Math.ceil(displayedMemoriesWithSearch.length / memoriesPageSize)
    )
    if (memoriesPage > maxMemoriesPage) setMemoriesPage(maxMemoriesPage)
  }, [displayedMemoriesWithSearch.length, memoriesPageSize, memoriesPage])

  useEffect(() => {
    if (!isIndexing) return
    setSelectedFileIds((prev) => {
      const next = new Set(
        [...prev].filter((id) => {
          const file = files.find((f) => f.id === id)
          return file ? isFileSelectable(file, isIndexing) : false
        })
      )
      return next.size === prev.size ? prev : next
    })
  }, [isIndexing, files])

  const closeDeleteModal = () => {
    if (isDeleting) return
    setDeleteModal({ isOpen: false, mode: 'single', fileId: null, fileName: '', count: 0 })
  }

  const handleDeleteFile = async () => {
    const ids =
      deleteModal.mode === 'bulk'
        ? [...selectedFileIds]
        : deleteModal.fileId != null
          ? [deleteModal.fileId]
          : []
    if (ids.length === 0) return

    setIsDeleting(true)
    let failed = 0
    try {
      for (const id of ids) {
        try {
          await deleteFile(id)
        } catch {
          failed += 1
        }
      }

      const deleted = ids.length - failed
      if (failed === 0) {
        toast.success(deleted === 1 ? 'File deleted' : `${deleted} files deleted`)
      } else if (deleted === 0) {
        toast.error(ids.length === 1 ? 'Failed to delete file' : 'Failed to delete files')
      } else {
        toast.error(`Deleted ${deleted} of ${ids.length} files (${failed} failed)`)
      }

      setDeleteModal({ isOpen: false, mode: 'single', fileId: null, fileName: '', count: 0 })
      if (deleted > 0) {
        setSelectedFileIds(new Set())
        await fetchData()
      }
    } finally {
      setIsDeleting(false)
    }
  }

  const openDeleteModal = (fileId: number, fileName: string) => {
    setDeleteModal({ isOpen: true, mode: 'single', fileId, fileName, count: 1 })
  }

  const openBulkDeleteModal = () => {
    if (selectedFileIds.size === 0) return
    setDeleteModal({
      isOpen: true,
      mode: 'bulk',
      fileId: null,
      fileName: '',
      count: selectedFileIds.size,
    })
  }

  const formatCleanupBytes = (raw: string): string => {
    const value = Number(raw)
    if (!Number.isFinite(value) || value <= 0) return '0 B'
    return formatFileSize(value)
  }

  const openCleanupModal = async () => {
    setIsLoadingCleanup(true)
    try {
      const preview = await previewProjectCleanup(projectId)
      const selectable = preview.options.filter((option) => option.itemCount > 0)
      if (selectable.length === 0) {
        toast.success('Nothing to clean up right now')
        return
      }
      const hasOldVersions = selectable.some((option) => option.option === 'oldFileVersions')
      setSelectedCleanupOptions(
        hasOldVersions
          ? new Set<ProjectCleanupOptionId>(['oldFileVersions'])
          : new Set(selectable.map((option) => option.option))
      )
      setCleanupPreview(preview)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to preview project cleanup')
    } finally {
      setIsLoadingCleanup(false)
    }
  }

  const toggleCleanupOption = (option: ProjectCleanupOptionId) => {
    setSelectedCleanupOptions((prev) => {
      const next = new Set(prev)
      if (next.has(option)) next.delete(option)
      else next.add(option)
      return next
    })
  }

  const handleRunCleanup = async () => {
    if (!cleanupPreview || selectedCleanupOptions.size === 0) return
    setIsCleaning(true)
    try {
      const result = await runProjectCleanup(projectId, [...selectedCleanupOptions])
      const removed = result.results.reduce((sum, row) => sum + row.removedCount, 0)
      toast.success(
        removed > 0
          ? `Cleaned up ${removed} item${removed === 1 ? '' : 's'}`
          : 'Cleanup finished — nothing was removed'
      )
      setCleanupPreview(null)
      setSelectedCleanupOptions(new Set())
      await fetchData()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to clean up project')
    } finally {
      setIsCleaning(false)
    }
  }

  const loadMemories = useCallback(async () => {
    if (!project) return
    setIsLoadingMemories(true)
    try {
      const result = await memoryApi.list(project.name)
      setMemories(result)
      setMemoriesLoaded(true)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load memories')
    } finally {
      setIsLoadingMemories(false)
    }
  }, [project])

  const loadGovernance = useCallback(async () => {
    if (!project) return
    try {
      const [{ sessions }, search] = await Promise.all([
        memoryV2Api.listV2Sessions(project.name),
        memoryV2Api.searchV2Memories(project.name, '*', undefined, 20),
      ])
      setV2Sessions(sessions)
      setV2Summaries(search.memories)
      setV2Degraded(search.degraded)
      setV2Warnings(search.warnings)
      setGovernanceLoaded(true)
      const first = sessions[0]
      if (first) {
        setSelectedSessionId(first.sessionId)
        const { handoffs } = await memoryV2Api.getV2Handoffs(project.name, first.sessionId)
        setV2Handoffs(handoffs)
      }
    } catch {
      // v2 endpoints are additive and may be unavailable on older servers —
      // the legacy memories list above stays the source of truth.
      setGovernanceLoaded(true)
    }
  }, [project])

  useEffect(() => {
    if (contentTab === 'memories' && !memoriesLoaded) {
      void loadMemories()
    }
    if (contentTab === 'memories' && !governanceLoaded) {
      void loadGovernance()
    }
  }, [contentTab, memoriesLoaded, loadMemories, governanceLoaded, loadGovernance])

  const handleAddMemory = async (text: string) => {
    if (!project) return
    try {
      await memoryApi.add(project.name, [{ role: 'user', content: text }])
      toast.success('Memory recorded')
      await loadMemories()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to record memory')
    }
  }

  const handleEditMemory = async (id: string, text: string) => {
    if (!project) return
    try {
      await memoryApi.update(project.name, id, text)
      toast.success('Memory updated')
      await loadMemories()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update memory')
    }
  }

  const handleDeleteMemory = async (id: string) => {
    if (!project) return
    try {
      await memoryApi.remove(project.name, id)
      toast.success('Memory forgotten')
      await loadMemories()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to forget memory')
    }
  }

  const handleSelectV2Session = async (sessionId: string) => {
    if (!project) return
    setSelectedSessionId(sessionId)
    try {
      const { handoffs } = await memoryV2Api.getV2Handoffs(project.name, sessionId)
      setV2Handoffs(handoffs)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load handoffs')
    }
  }

  const handleVerifyV2Memory = async (memoryId: string) => {
    if (!project) return
    try {
      const [verify, timeline] = await Promise.all([
        memoryV2Api.getV2Verify(project.name, memoryId),
        memoryV2Api.getV2Timeline(project.name, { memoryId }),
      ])
      setV2Verify(verify)
      setV2Timeline(timeline.items)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to verify memory')
    }
  }

  const handleArchiveV2Memory = async (memoryId: string) => {
    if (!project) return
    try {
      await memoryV2Api.archiveV2Memory(project.name, memoryId)
      toast.success('Memory archived — restore is available from history')
      await loadGovernance()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to archive memory')
    }
  }

  const handleRestoreV2Memory = async (memoryId: string) => {
    if (!project) return
    try {
      await memoryV2Api.restoreV2Memory(project.name, memoryId)
      toast.success('Memory restored')
      await loadGovernance()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to restore memory')
    }
  }

  const refreshLiveData = useCallback(async () => {
    const [projectData, filesData] = await Promise.all([
      getProject(projectId),
      getProjectFiles(projectId),
    ])
    setProject(projectData)
    setFiles(filesData)
  }, [projectId])

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => stopPolling()
  }, [stopPolling])

  const pollJob = useCallback(
    (jobId: number) => {
      stopPolling()
      setIsIndexing(true)
      lastFilesProcessedRef.current = -1
      lastFileRefreshRef.current = 0
      lastPhaseRef.current = null
      lastCurrentFileRef.current = null
      let consecutiveErrors = 0
      const MAX_POLL_ERRORS = 5 // tolerate up to 5 transient failures before aborting

      const poll = async () => {
        try {
          const job = await getJobStatus(jobId)
          consecutiveErrors = 0
          setIndexingJob(job)

          const now = Date.now()
          const filesChanged = job.filesProcessed !== lastFilesProcessedRef.current
          const phaseChanged = job.phase !== lastPhaseRef.current
          const currentChanged = job.currentFileId !== lastCurrentFileRef.current
          if (
            filesChanged ||
            phaseChanged ||
            currentChanged ||
            now - lastFileRefreshRef.current > 10_000
          ) {
            lastFilesProcessedRef.current = job.filesProcessed
            lastPhaseRef.current = job.phase
            lastCurrentFileRef.current = job.currentFileId
            lastFileRefreshRef.current = now
            void refreshLiveData()
          }

          if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
            stopPolling()
            setIsIndexing(false)
            setIndexingJob(null)
            await refreshLiveData()
            if (job.status === 'completed') {
              toast.success('Indexing completed!')
            } else if (job.status === 'failed') {
              toast.error(`Indexing failed: ${job.errorMessage}`)
            }
            // cancelled: toast already shown by stop handler
          }
        } catch {
          consecutiveErrors++
          // Rate-limit or transient errors — back off and retry
          if (consecutiveErrors >= MAX_POLL_ERRORS) {
            stopPolling()
            setIsIndexing(false)
            setIndexingJob(null)
            toast.error('Polling stopped due to repeated errors. Reload to check status.')
          }
          // Otherwise skip this tick — next poll will retry
        }
      }

      void poll()
      pollIntervalRef.current = setInterval(poll, 2000)
    },
    [refreshLiveData, stopPolling]
  )

  // Always restore an in-flight job after refresh (do not require indexing files first)
  const hasResumedRef = useRef(false)
  useEffect(() => {
    if (isLoading || isIndexing || hasResumedRef.current) return
    hasResumedRef.current = true
    let cancelled = false
    ;(async () => {
      try {
        const job = await getActiveJob(projectId)
        if (cancelled || !job) return
        setIndexingJob(job)
        pollJob(job.id)
      } catch {
        // Leave UI idle; user can still see stuck/pending banners
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isLoading, isIndexing, projectId, pollJob])

  const startIndexingJob = useCallback(
    async (
      startJob: () => Promise<{ jobId: number; filesQueued: number }>,
      successMessage: string
    ) => {
      setIsIndexing(true)
      try {
        const result = await startJob()
        toast.success(`${successMessage}: ${result.filesQueued} file(s) queued`)
        pollJob(result.jobId)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to start indexing')
        setIsIndexing(false)
      }
    },
    [pollJob]
  )

  const handleIndex = async (fileIds?: number[]) => {
    await startIndexingJob(() => indexProject(projectId, fileIds), 'Indexing started')
  }

  const handleRetryFailed = async (fileIds?: number[]) => {
    const ids =
      fileIds ??
      files
        .filter((f) => f.indexingStatus === 'failed' || f.indexingStatus === 'indexing')
        .map((f) => f.id)
    if (ids.length === 0) return

    await startIndexingJob(() => indexProject(projectId, ids), 'Retry started')
  }

  const handleRetryStuck = async (fileIds?: number[]) => {
    const ids = fileIds ?? files.filter((f) => f.indexingStatus === 'indexing').map((f) => f.id)
    if (ids.length === 0) return

    await startIndexingJob(() => indexProject(projectId, ids), 'Retry started')
  }

  const handleReindexFile = async (fileId: number) => {
    await startIndexingJob(() => reindexFile(fileId), 'Reindex started')
  }

  const handleReindexAll = async () => {
    setReindexAllModal(false)
    await startIndexingJob(() => reindexProject(projectId), 'Reindex started')
  }

  const executeStopIndexing = async () => {
    if (isStopping) return
    setIsStopping(true)
    setStopConfirmOpen(false)
    try {
      await stopProjectIndex(projectId)
      stopPolling()
      setIsIndexing(false)
      setIndexingJob(null)
      await refreshLiveData()
      toast.success('Indexing stopped')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to stop indexing')
    } finally {
      setIsStopping(false)
    }
  }

  const handleStopIndexing = () => {
    if (indexingJob?.phase === 'enriching') {
      setStopConfirmOpen(true)
      return
    }
    void executeStopIndexing()
  }

  const openSettingsModal = async () => {
    if (!project) return
    setEditDescription(project.description ?? '')
    setEditContextualEnrichment(project.contextualEnrichmentEnabled ?? false)
    setEditTranscriptDistillation(project.transcriptDistillationEnabled ?? false)
    setEmbeddingConfirm(false)
    setSelectedEmbeddingProviderId('')
    if (project.embedding) {
      let fetchedSettings: Awaited<ReturnType<typeof getSettings>> | null = null
      try {
        fetchedSettings = await getSettings()
        setAccountSettings(fetchedSettings)
      } catch {
        setAccountSettings(null)
      }
      try {
        const rows = await listEmbeddingProviders()
        // R8: hide the `local` option for new selections when the platform
        // disables it (the project's current row keeps working regardless).
        const localOk = fetchedSettings?.localEmbeddingSupported ?? true
        setSystemEmbeddingProviders(
          rows.filter((r) => r.isActive && (localOk || r.provider !== 'local'))
        )
      } catch {
        setSystemEmbeddingProviders([])
      }
    }
    setEmbeddingModal(true)
  }

  const handleSaveEnrichmentToggle = async () => {
    if (!project) return
    setIsSavingEnrichmentToggle(true)
    try {
      const updated = await updateProject(projectId, {
        contextualEnrichmentEnabled: editContextualEnrichment,
      })
      setProject({
        ...project,
        contextualEnrichmentEnabled:
          updated.contextualEnrichmentEnabled ?? editContextualEnrichment,
      })
      toast.success('Enrichment setting saved')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save enrichment setting')
    } finally {
      setIsSavingEnrichmentToggle(false)
    }
  }

  const handleSaveDescription = async () => {
    if (!project) return
    setIsSavingDescription(true)
    try {
      const trimmed = editDescription.trim()
      const updated = await updateProject(projectId, {
        description: trimmed === '' ? null : trimmed,
      })
      setProject({ ...project, description: updated.description })
      toast.success('Description saved')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save description')
    } finally {
      setIsSavingDescription(false)
    }
  }

  const handleSaveEmbedding = async () => {
    if (!project || selectedEmbeddingProviderId === '') return
    setIsSavingEmbedding(true)
    try {
      await relinkProjectEmbeddingProvider(projectId, selectedEmbeddingProviderId)
      setEmbeddingModal(false)
      setEmbeddingConfirm(false)
      toast.success('Embedding provider updated')
      await fetchData()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update embedding provider')
    } finally {
      setIsSavingEmbedding(false)
    }
  }

  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !project) return
    setIsUploadingIcon(true)
    try {
      const { iconUrl } = await uploadProjectIcon(project.id, file)
      setProject({ ...project, iconUrl })
      toast.success('Icon uploaded')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to upload icon')
    } finally {
      setIsUploadingIcon(false)
      if (iconInputRef.current) iconInputRef.current.value = ''
    }
  }

  const handleIconDelete = async () => {
    if (!project) return
    setIsUploadingIcon(true)
    try {
      await deleteProjectIcon(project.id)
      setProject({ ...project, iconUrl: null })
      toast.success('Icon removed')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove icon')
    } finally {
      setIsUploadingIcon(false)
    }
  }

  const clearSelection = () => setSelectedFileIds(new Set())

  const toggleFileSelection = (fileId: number) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev)
      if (next.has(fileId)) next.delete(fileId)
      else next.add(fileId)
      return next
    })
  }

  const handleTestSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!project || !testQuery.trim()) return

    setIsSearching(true)
    setHasSearched(true)
    setSearchWarning(null)
    setAiAnswer('')
    setAnswerError(null)
    setActiveResultTab('sources')

    try {
      const response = await searchProject(project.name, testQuery, testLimit, {
        docTypes: testDocTypes,
        tags: testTags,
      })
      setTestResults(response.results)
      setSearchWarning(response.warning ?? null)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Search failed')
      setTestResults([])
      setSearchWarning(null)
    } finally {
      setIsSearching(false)
    }
  }

  const handleInferFilters = async () => {
    if (!project || !testQuery.trim()) return
    setIsInferring(true)
    try {
      const result = await parseFilters(project.name, testQuery)
      if (result.docTypes.length > 0 || result.tags.length > 0) {
        setTestDocTypes(toDocTypes(result.docTypes))
        setTestTags(result.tags)
      }
      if (result.docTypes.length === 0 && result.tags.length === 0) {
        setInferredNote('No filters inferred')
      } else {
        const parts: string[] = []
        if (result.docTypes.length) parts.push(result.docTypes.join(', '))
        if (result.tags.length) parts.push(`tags: ${result.tags.join(', ')}`)
        setInferredNote(`Inferred: ${parts.join(' · ')}`)
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to infer filters')
    } finally {
      setIsInferring(false)
    }
  }

  const handleGenerateAnswer = async () => {
    if (!project || !testQuery.trim()) return

    setIsGeneratingAnswer(true)
    setHasSearched(true)
    setTestResults([])
    setSearchWarning(null)
    setAiAnswer('')
    setAnswerError(null)
    setActiveResultTab('answer')

    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      await streamAnswer(
        project.name,
        testQuery,
        testLimit,
        (chunk: AnswerChunk) => {
          if (chunk.type === 'sources') {
            setTestResults(chunk.results ?? [])
          } else if (chunk.type === 'content' && chunk.content) {
            setAiAnswer((prev) => prev + chunk.content)
          } else if (chunk.type === 'error') {
            setAnswerError(chunk.message ?? 'Unknown error from LLM')
          }
        },
        controller.signal,
        { docTypes: testDocTypes, tags: testTags }
      )
    } catch (error) {
      if (!controller.signal.aborted) {
        setAnswerError(error instanceof Error ? error.message : 'Failed to generate answer')
      }
    } finally {
      setIsGeneratingAnswer(false)
      abortControllerRef.current = null
    }
  }

  const handleStopAnswer = () => {
    abortControllerRef.current?.abort()
    setIsGeneratingAnswer(false)
  }

  const handleDownload = async (file: FileResponse) => {
    try {
      await downloadFile(file.id, file.originalFilename)
    } catch {
      toast.error('Failed to download file')
    }
  }

  const openReplacePicker = (file: FileResponse) => {
    setReplaceTarget(file)
    replaceInputRef.current?.click()
  }

  const handleReplaceSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFile = e.target.files?.[0]
    e.target.value = ''
    if (!newFile || !replaceTarget) return

    const target = replaceTarget
    setReplacingFileId(target.id)
    const toastId = toast.loading(`Replacing "${target.originalFilename}"…`)
    try {
      await replaceFile(target.id, newFile)
      toast.success(`"${newFile.name}" replaced successfully`, { id: toastId })
      setReplaceTarget(null)
      await fetchData()
      setReindexModal({
        isOpen: true,
        fileId: target.id,
        fileName: newFile.name,
        afterReplace: true,
      })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to replace file', {
        id: toastId,
      })
    } finally {
      setReplacingFileId(null)
    }
  }

  const handleReindexAfterReplace = async () => {
    if (!reindexModal.fileId) return

    const fileId = reindexModal.fileId
    setReindexModal({ isOpen: false, fileId: null, fileName: '' })
    await handleIndex([fileId])
  }

  const selectableFiles = useMemo(
    () => displayedFiles.filter((f) => isFileSelectable(f, isIndexing)),
    [displayedFiles, isIndexing]
  )
  const selectedFiles = useMemo(
    () => files.filter((f) => selectedFileIds.has(f.id)),
    [files, selectedFileIds]
  )
  const selectedPending = selectedFiles.filter((f) => f.indexingStatus === 'pending')
  const selectedIndexed = selectedFiles.filter((f) => f.indexingStatus === 'indexed')
  const selectedRetryable = selectedFiles.filter(
    (f) => f.indexingStatus === 'failed' || (f.indexingStatus === 'indexing' && !isIndexing)
  )
  const allSelectableSelected =
    selectableFiles.length > 0 && selectableFiles.every((f) => selectedFileIds.has(f.id))
  const someSelectableSelected = selectableFiles.some((f) => selectedFileIds.has(f.id))

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelectableSelected && !allSelectableSelected
    }
  }, [someSelectableSelected, allSelectableSelected])

  const handleSelectAll = () => {
    if (allSelectableSelected) {
      clearSelection()
      return
    }
    setSelectedFileIds(new Set(selectableFiles.map((f) => f.id)))
  }

  const handleIndexSelected = async () => {
    const ids = selectedPending.map((f) => f.id)
    if (ids.length === 0) return
    clearSelection()
    await handleIndex(ids)
  }

  const handleReindexSelected = async () => {
    const ids = selectedIndexed.map((f) => f.id)
    if (ids.length === 0) return
    clearSelection()
    await startIndexingJob(() => reindexProject(projectId, ids), 'Reindex started')
  }

  const handleRetrySelected = async () => {
    const ids = selectedRetryable.map((f) => f.id)
    if (ids.length === 0) return
    clearSelection()
    await startIndexingJob(() => indexProject(projectId, ids), 'Retry started')
  }

  const canManage = canManageProject(project?.projectRole)
  const globalAdmin = isGlobalAdmin(user?.role)

  useEffect(() => {
    if (!canManage || !Number.isFinite(projectId)) {
      setHeaderProxyTokens(null)
      return
    }
    let cancelled = false
    getProjectTokenSavings(projectId, { hours: 24 })
      .then((report) => {
        if (!cancelled) setHeaderProxyTokens(report.proxy.proxyTokens)
      })
      .catch(() => {
        if (!cancelled) setHeaderProxyTokens(null)
      })
    return () => {
      cancelled = true
    }
  }, [canManage, projectId])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500" />
      </div>
    )
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-harbor-text-secondary">Project not found</p>
        <Link to="/projects" className="text-emerald-400 hover:text-emerald-300 mt-2 inline-block">
          Back to projects
        </Link>
      </div>
    )
  }

  const pendingFiles = files.filter((f) => f.indexingStatus === 'pending')
  const stuckFiles = files.filter((f) => f.indexingStatus === 'indexing' && !isIndexing)
  const indexedFiles = files.filter((f) => f.indexingStatus === 'indexed')
  const failedFiles = files.filter((f) => f.indexingStatus === 'failed')
  const hasPendingFiles = pendingFiles.length > 0
  const hasFailedFiles = failedFiles.length > 0
  const hasStuckFiles = stuckFiles.length > 0
  const hasIndexedFiles = indexedFiles.length > 0
  const showReindexAll =
    files.length > 0 && (hasIndexedFiles || hasStuckFiles || hasFailedFiles) && !isIndexing
  const canReindexAll = files.length > 0 && !isIndexing
  const indexingProgress = indexingJob
    ? Math.min(indexingJob.filesProcessed, indexingJob.filesQueued)
    : 0
  const chunkProgressPct =
    indexingJob && indexingJob.chunksTotal > 0
      ? (indexingJob.chunksDone / indexingJob.chunksTotal) * 100
      : null
  const fileProgressPct =
    indexingJob && indexingJob.filesQueued > 0
      ? (indexingProgress / indexingJob.filesQueued) * 100
      : 0
  const barPct = chunkProgressPct ?? fileProgressPct
  const heartbeatStale =
    isIndexing &&
    indexingJob?.status === 'running' &&
    indexingJob.heartbeatAt != null &&
    Date.now() - new Date(indexingJob.heartbeatAt).getTime() > STALE_HEARTBEAT_MS
  const summary = indexingJob?.filesSummary
  const readyCount = summary?.ready ?? indexedFiles.length
  const failedCount = summary?.failed ?? failedFiles.length

  return (
    <div className="space-y-6">
      <input
        ref={replaceInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.md,.txt,.html,.docx,.pptx,.json"
        aria-label="Replace document file"
        onChange={handleReplaceSelected}
      />

      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <Link to="/projects" className="text-harbor-text-secondary hover:text-harbor-text">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </Link>
            {(() => {
              const authIconUrl = getAuthenticatedIconUrl(project.iconUrl)
              return authIconUrl && failedIconUrl !== authIconUrl ? (
                <img
                  src={authIconUrl}
                  alt=""
                  className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                  onError={() => setFailedIconUrl(authIconUrl)}
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-emerald-600/20 text-emerald-400 flex items-center justify-center text-lg font-bold flex-shrink-0">
                  {project.name[0]?.toUpperCase() ?? '?'}
                </div>
              )
            })()}
            <h1 className="text-2xl font-bold text-harbor-text">{project.name}</h1>
            {project.projectRole && (
              <span className="text-xs px-2 py-1 rounded-md bg-harbor-surface-raised text-harbor-text-secondary">
                {projectRoleLabel(project.projectRole)}
              </span>
            )}
            {canManage && (
              <button
                type="button"
                onClick={() => void openSettingsModal()}
                className="p-1.5 text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors"
                title="Project settings"
                aria-label="Project settings"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </button>
            )}
          </div>
          {project.embedding ? (
            <p className="text-sm text-harbor-text-secondary mt-2 ml-8">
              {project.embedding.provider === 'local' ? (
                <span className="inline-block mr-2 text-xs px-1.5 py-0.5 rounded bg-harbor-border/60 text-harbor-text-secondary">
                  Private retrieval
                </span>
              ) : (
                <span
                  className="inline-block mr-2 text-xs px-1.5 py-0.5 rounded bg-violet-900/40 text-violet-300"
                  title="Document text and queries from this project are processed by a third-party inference provider"
                >
                  Premium retrieval — Qwen3 (hosted inference)
                </span>
              )}
              {project.embedding.provider === 'local' ? 'Local' : project.embedding.provider}
              {' · '}
              {project.embedding.modelName}
              {' · '}
              {project.embedding.dimensions} dimensions
            </p>
          ) : (
            <p className="text-sm text-amber-400/90 mt-2 ml-8">
              Legacy project · embedding configuration not recorded
            </p>
          )}
          {project.description && (
            <p className="text-harbor-text-secondary mt-2 ml-8">{project.description}</p>
          )}
        </div>
        <div className="flex gap-3 flex-shrink-0 flex-wrap justify-end">
          <Link
            to="/setup/mcp"
            className="px-4 py-2 bg-harbor-surface-raised border border-harbor-border text-harbor-text text-sm font-medium rounded-lg hover:bg-harbor-border transition-colors"
          >
            MCP Setup
          </Link>
          {canManage && (
            <Button
              onClick={() => void openCleanupModal()}
              disabled={isIndexing || isLoadingCleanup}
              variant="secondary"
              title={isIndexing ? 'Cleanup disabled while indexing' : undefined}
            >
              {isLoadingCleanup ? 'Checking…' : 'Clean up project'}
            </Button>
          )}
          {canManage && (
            <>
              {isIndexing ? (
                <Button disabled variant="secondary" title="Uploads disabled while indexing">
                  Upload Files
                </Button>
              ) : (
                <Link
                  to={`/projects/${projectId}/upload`}
                  className="px-4 py-2 bg-harbor-border text-harbor-text text-sm font-medium rounded-lg hover:bg-harbor-border transition-colors"
                >
                  Upload Files
                </Link>
              )}
              {hasFailedFiles && (
                <Button
                  onClick={() => handleRetryFailed()}
                  disabled={isIndexing}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {isIndexing ? 'Indexing...' : `Retry failed (${failedFiles.length})`}
                </Button>
              )}
              {hasStuckFiles && (
                <Button
                  onClick={() => handleRetryStuck()}
                  disabled={isIndexing}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {isIndexing ? 'Indexing...' : `Retry stuck (${stuckFiles.length})`}
                </Button>
              )}
              {showReindexAll && (
                <Button
                  onClick={() => setReindexAllModal(true)}
                  disabled={isIndexing || !canReindexAll}
                  variant="secondary"
                >
                  Reindex all
                </Button>
              )}
              <Button
                onClick={() => (isIndexing ? handleStopIndexing() : handleIndex())}
                disabled={isIndexing ? isStopping : !hasPendingFiles}
                className={
                  isIndexing
                    ? 'bg-red-600 hover:bg-red-700'
                    : hasPendingFiles && !isIndexing && highlightIndex
                      ? 'ring-2 ring-emerald-400 ring-offset-2 ring-offset-harbor-ring-offset animate-pulse'
                      : hasPendingFiles && !isIndexing
                        ? 'ring-2 ring-emerald-400 ring-offset-2 ring-offset-harbor-ring-offset'
                        : undefined
                }
              >
                {isIndexing
                  ? isStopping
                    ? 'Stopping…'
                    : 'Stop indexing'
                  : `Index unindexed (${pendingFiles.length})`}
              </Button>
            </>
          )}
        </div>
      </div>

      {project.embedding && (
        <div
          className={
            project.embedding.configured
              ? 'rounded-xl border border-harbor-border bg-harbor-surface/40 px-4 py-3'
              : 'rounded-xl border border-amber-500/50 bg-amber-500/10 px-4 py-3'
          }
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="space-y-1 min-w-0">
              <p
                className={
                  project.embedding.configured
                    ? 'text-sm font-medium text-harbor-text'
                    : 'text-sm font-medium text-amber-100'
                }
              >
                {project.embedding.configured
                  ? 'Project embedding'
                  : 'Using Account Settings (legacy)'}
              </p>
              {!project.embedding.configured && (
                <p className="text-sm text-amber-200/90">
                  This project inherits account embedding settings. Configure a project overlay so
                  Settings changes cannot silently reassign its vector space.
                </p>
              )}
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm mt-1">
                <div>
                  <dt className="text-harbor-text-muted inline">Provider </dt>
                  <dd className="text-harbor-text inline">
                    {providerLabel(project.embedding.provider)}
                  </dd>
                </div>
                <div>
                  <dt className="text-harbor-text-muted inline">Model </dt>
                  <dd className="text-harbor-text font-mono text-xs inline break-all">
                    {project.embedding.modelName}
                  </dd>
                </div>
                {project.embedding.apiBaseUrl && (
                  <div>
                    <dt className="text-harbor-text-muted inline">Endpoint </dt>
                    <dd className="text-harbor-text font-mono text-xs inline break-all">
                      {project.embedding.apiBaseUrl}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-harbor-text-muted inline">Dimensions </dt>
                  <dd className="text-harbor-text inline">
                    {project.embedding.dimensions ?? 'unknown'}
                  </dd>
                </div>
              </dl>
            </div>
            {canManage && (
              <Button
                size="sm"
                variant="secondary"
                disabled={isIndexing}
                onClick={() => void openSettingsModal()}
                title={isIndexing ? 'Disabled while indexing' : undefined}
              >
                {globalAdmin && project.embedding.configured
                  ? 'Edit embedding'
                  : globalAdmin
                    ? 'Configure embedding'
                    : 'Edit description'}
              </Button>
            )}
          </div>
        </div>
      )}

      {isIndexing && indexingJob && (
        <div className="rounded-xl border border-blue-500/50 bg-blue-500/10 px-4 py-4 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium text-blue-100">Indexing in progress</p>
              <span className="text-xs px-2 py-0.5 rounded-md bg-blue-950/70 text-blue-200 border border-blue-500/40 uppercase tracking-wide">
                {indexingJob.phase}
              </span>
            </div>
            <div className="flex items-center gap-3 flex-wrap justify-end">
              <p className="text-xs text-blue-300/70">
                {readyCount} ready · {failedCount} failed ·{' '}
                {indexingJob.chunksCreated + indexingJob.chunksDone} chunks
              </p>
              {canManage && (
                <Button
                  size="sm"
                  onClick={handleStopIndexing}
                  disabled={isStopping}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {isStopping ? 'Stopping…' : 'Stop indexing'}
                </Button>
              )}
            </div>
          </div>
          <p className="text-sm text-blue-200">
            Files {indexingProgress} / {indexingJob.filesQueued}
          </p>
          {indexingJob.activeFiles.length > 0 && (
            <div className="space-y-1">
              {indexingJob.activeFiles.map((f) => (
                <p key={f.fileId} className="text-xs text-blue-300/80 truncate">
                  <span className="uppercase tracking-wide text-blue-300/60">{f.phase}</span>
                  {' · '}
                  {f.filename}
                  {f.chunksTotal > 0 ? (
                    <span>
                      {' · '}
                      Chunks {f.chunksDone} / {f.chunksTotal}
                    </span>
                  ) : null}
                </p>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-blue-300/70">
            <span>Elapsed {formatElapsed(indexingJob.elapsedMs)}</span>
            <span>ETA {formatEta(indexingJob.etaSeconds)}</span>
          </div>
          <div className="h-1.5 rounded-full bg-blue-950/60 overflow-hidden">
            <div
              className="h-full bg-blue-400 transition-all duration-500 ease-out"
              style={{ width: `${Math.min(100, Math.max(0, barPct))}%` }}
            />
          </div>
          {heartbeatStale && (
            <p className="text-sm text-amber-200">
              Worker may have stalled (no heartbeat for 60s). Use Retry stuck or reload after
              recovering the job.
            </p>
          )}
        </div>
      )}

      {canManage && hasStuckFiles && !isIndexing && (
        <div className="rounded-xl border border-amber-500/50 bg-amber-500/10 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-amber-200">
            {stuckFiles.length} file(s) appear stuck in indexing. Retry to re-process them.
          </p>
          <Button
            size="sm"
            onClick={() => handleRetryStuck()}
            className="bg-amber-600 hover:bg-amber-700"
          >
            Retry stuck ({stuckFiles.length})
          </Button>
        </div>
      )}

      {canManage && hasFailedFiles && !isIndexing && (
        <div className="rounded-xl border border-red-500/50 bg-red-500/10 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-red-200">
            {failedFiles.length} file(s) failed to index. Review the errors below and retry when
            ready.
          </p>
          <Button
            size="sm"
            onClick={() => handleRetryFailed()}
            className="bg-red-600 hover:bg-red-700"
          >
            Retry failed ({failedFiles.length})
          </Button>
        </div>
      )}

      {canManage && hasPendingFiles && !isIndexing && (
        <div
          className={`rounded-xl border px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${
            highlightIndex
              ? 'border-2 border-emerald-500 bg-emerald-500/10 shadow-lg shadow-emerald-500/10'
              : 'border-emerald-500/50 bg-emerald-500/10'
          }`}
        >
          <p className="text-sm text-emerald-200">
            {pendingFiles.length} file(s) uploaded and waiting to be indexed.
          </p>
          <Button size="sm" onClick={() => handleIndex()}>
            Index unindexed now
          </Button>
        </div>
      )}

      <div className={`grid grid-cols-1 sm:grid-cols-4 gap-4 ${canManage ? 'lg:grid-cols-5' : ''}`}>
        <div className="bg-harbor-surface border border-harbor-border rounded-xl p-6">
          <p className="text-sm font-medium text-harbor-text-secondary">Total Files</p>
          <p className="mt-2 text-3xl font-bold text-harbor-text">{project.stats.documentCount}</p>
          <p className="text-xs text-harbor-text-muted mt-1">
            {project.storage ? formatFileSize(project.storage.totalFilesBytes) : '—'}
          </p>
        </div>
        <div className="bg-harbor-surface border border-harbor-border rounded-xl p-6">
          <p className="text-sm font-medium text-harbor-text-secondary">Total Chunks</p>
          <p className="mt-2 text-3xl font-bold text-harbor-text">
            {files.reduce((sum, f) => sum + f.chunkCount, 0)}
          </p>
          <p className="text-xs text-harbor-text-muted mt-1">
            {project.storage ? formatFileSize(project.storage.totalVectorBytes) : '—'}
          </p>
        </div>
        <div className="bg-harbor-surface border border-harbor-border rounded-xl p-6">
          <p className="text-sm font-medium text-harbor-text-secondary">Ready</p>
          <p className="mt-2 text-3xl font-bold text-emerald-400">{indexedFiles.length}</p>
        </div>
        <div className="bg-harbor-surface border border-harbor-border rounded-xl p-6">
          <p className="text-sm font-medium text-harbor-text-secondary">Failed</p>
          <p
            className={`mt-2 text-3xl font-bold ${failedFiles.length > 0 ? 'text-red-400' : 'text-harbor-text'}`}
          >
            {failedFiles.length}
          </p>
        </div>
        {canManage && (
          <div className="bg-harbor-surface border border-harbor-border rounded-xl p-6">
            <p className="text-sm font-medium text-harbor-text-secondary">Tokens saved (proxy)</p>
            <p className="mt-2 text-3xl font-bold text-amber-400">
              {headerProxyTokens === null ? '—' : headerProxyTokens.toLocaleString('en-US')}
            </p>
            <p className="text-xs text-harbor-text-muted mt-1">
              Last 24h · upper-bound estimate, not exact
            </p>
          </div>
        )}
      </div>

      {/* Test RAG — Collapsible */}
      <div className="bg-harbor-surface border border-harbor-border rounded-xl">
        <button
          type="button"
          onClick={() => setTestRagExpanded((prev) => !prev)}
          className="w-full p-6 flex items-center justify-between text-left hover:bg-harbor-surface-raised/30 transition-colors rounded-xl"
        >
          <div>
            <h2 className="text-lg font-semibold text-harbor-text flex items-center gap-2">
              <span>🧪</span> Test RAG
            </h2>
            <p className="text-sm text-harbor-text-secondary mt-1">
              Run a semantic search query against this project's indexed chunks.
            </p>
          </div>
          <svg
            className={`w-5 h-5 text-harbor-text-secondary flex-shrink-0 transition-transform duration-200 ${
              testRagExpanded ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {testRagExpanded && (
          <>
            <form onSubmit={handleTestSearch} className="px-6 pb-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
                    Query
                  </label>
                  <input
                    type="text"
                    value={testQuery}
                    onChange={(e) => setTestQuery(e.target.value)}
                    required
                    disabled={!hasIndexedFiles}
                    className="w-full px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder={
                      hasIndexedFiles
                        ? 'What are you looking for?'
                        : 'Index files first to enable search'
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
                    Limit
                  </label>
                  <input
                    type="number"
                    value={testLimit}
                    onChange={(e) => setTestLimit(Number(e.target.value))}
                    min={1}
                    max={100}
                    disabled={!hasIndexedFiles}
                    className="w-full px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div className="flex flex-col xl:flex-row items-stretch xl:items-end justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleGenerateAnswer}
                    disabled={
                      isGeneratingAnswer || isSearching || !hasIndexedFiles || !testQuery.trim()
                    }
                    className="h-10 flex-1 px-4 inline-flex items-center justify-center gap-2 bg-harbor-surface-raised border border-emerald-500/60 text-emerald-300 text-sm font-medium rounded-lg hover:bg-emerald-500/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                  >
                    <span aria-hidden="true">✨</span>
                    {isGeneratingAnswer ? 'Generating...' : 'Generate Answer'}
                  </button>
                  <button
                    type="submit"
                    disabled={
                      isSearching || isGeneratingAnswer || !hasIndexedFiles || !testQuery.trim()
                    }
                    className="h-10 flex-1 px-4 inline-flex items-center justify-center bg-emerald-600 text-harbor-text text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSearching ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </div>
              <div className="mt-4">
                <SearchFiltersPanel
                  docTypes={testDocTypes}
                  onDocTypesChange={(v) => {
                    setTestDocTypes(v)
                    setInferredNote(null)
                  }}
                  tags={testTags}
                  onTagsChange={(v) => {
                    setTestTags(v)
                    setInferredNote(null)
                  }}
                  tagSuggestions={tagSuggestions}
                  disabled={!testQuery.trim() || !hasIndexedFiles}
                  onInfer={handleInferFilters}
                  isInferring={isInferring}
                  inferredNote={inferredNote}
                />
              </div>
            </form>

            {hasSearched && (
              <div
                role="tablist"
                aria-label="RAG response"
                className="px-6 border-t border-b border-harbor-border flex gap-6"
              >
                {(['answer', 'sources'] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    aria-selected={activeResultTab === tab}
                    onClick={() => setActiveResultTab(tab)}
                    className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeResultTab === tab
                        ? 'border-emerald-500 text-emerald-300'
                        : 'border-transparent text-harbor-text-secondary hover:text-harbor-text'
                    }`}
                  >
                    {tab === 'answer' ? 'Answer' : `Sources (${testResults.length})`}
                  </button>
                ))}
              </div>
            )}

            {hasSearched && activeResultTab === 'sources' && (
              <div>
                <div className="px-6 py-4">
                  <h3 className="text-sm font-semibold text-harbor-text">
                    Results ({testResults.length})
                  </h3>
                </div>

                {testResults.length === 0 ? (
                  <div className="px-6 pb-6 text-center">
                    <div className="text-4xl mb-3">🔍</div>
                    <p className="text-sm text-harbor-text-secondary">
                      {searchWarning || 'No results found. Try a different query.'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-harbor-border">
                    {searchWarning && (
                      <div className="px-6 py-3">
                        <p className="text-sm text-amber-400">{searchWarning}</p>
                      </div>
                    )}
                    {testResults.map((result, index) => (
                      <div key={index} className="px-6 py-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-mono text-harbor-text-secondary">
                              {result.filename}
                            </span>
                            <span className="text-xs text-harbor-text-muted">
                              chunk #{result.chunkIndex}
                            </span>
                          </div>
                          <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded">
                            {formatMatchPercent(result.score)}% match
                          </span>
                        </div>
                        <div className="bg-harbor-surface-raised rounded-lg p-3">
                          <pre className="text-sm text-harbor-text-secondary whitespace-pre-wrap font-sans">
                            {result.content}
                          </pre>
                        </div>
                        <p className="mt-1 text-xs text-harbor-text-muted">
                          Source: {result.source}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* AI Answer Section */}
            {hasSearched && activeResultTab === 'answer' && (
              <div>
                <div className="px-6 py-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-harbor-text flex items-center gap-2">
                      <span className="text-emerald-400">✨</span>
                      AI Answer
                    </h3>
                    <p className="text-xs text-harbor-text-secondary mt-0.5">
                      Knowledge-based answer from your indexed documents
                    </p>
                  </div>
                  {isGeneratingAnswer ? (
                    <button
                      type="button"
                      onClick={handleStopAnswer}
                      className="px-3 py-1.5 bg-red-600/80 text-harbor-text text-xs font-medium rounded-lg hover:bg-red-600 transition-colors"
                    >
                      Stop
                    </button>
                  ) : !aiAnswer && !answerError ? (
                    <button
                      type="button"
                      onClick={handleGenerateAnswer}
                      className="px-3 py-1.5 bg-emerald-600 text-harbor-text text-xs font-medium rounded-lg hover:bg-emerald-700 transition-colors"
                    >
                      <span aria-hidden="true" className="mr-1.5">
                        ✨
                      </span>
                      Generate Answer
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleGenerateAnswer}
                      className="px-3 py-1.5 bg-harbor-border text-harbor-text text-xs font-medium rounded-lg hover:bg-harbor-border transition-colors"
                    >
                      <span aria-hidden="true" className="mr-1.5">
                        ✨
                      </span>
                      Regenerate
                    </button>
                  )}
                </div>

                {answerError && (
                  <div className="px-6 pb-4">
                    <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-3">
                      <p className="text-xs text-red-300">
                        <span className="font-medium">Error:</span> {answerError}
                      </p>
                    </div>
                  </div>
                )}

                {(aiAnswer || isGeneratingAnswer) && (
                  <div className="px-6 pb-6">
                    <div className="bg-harbor-surface-raised/50 rounded-lg p-4">
                      <div className="text-sm text-harbor-text whitespace-pre-wrap leading-relaxed">
                        {aiAnswer}
                        {isGeneratingAnswer && (
                          <span className="inline-block w-2 h-4 ml-0.5 bg-emerald-400 animate-pulse" />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Token savings — manager-only, project-scoped (Task 7) */}
      {canManage && <ProjectTokenSavingsPanel projectId={projectId} />}

      {/* Files / Connectors tabs — tab persisted in URL (?tab=) */}
      <div
        className="flex border-b border-harbor-border mb-0"
        role="tablist"
        aria-label="Project sections"
      >
        <button
          aria-selected={contentTab === 'files'}
          onClick={() => setContentTab('files')}
          className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
            contentTab === 'files'
              ? 'border-emerald-500 text-emerald-400'
              : 'border-transparent text-harbor-text-secondary hover:text-harbor-text'
          }`}
        >
          Files
        </button>
        <button
          aria-selected={contentTab === 'connectors'}
          onClick={() => setContentTab('connectors')}
          className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
            contentTab === 'connectors'
              ? 'border-emerald-500 text-emerald-400'
              : 'border-transparent text-harbor-text-secondary hover:text-harbor-text'
          }`}
        >
          Connectors
        </button>
        <button
          aria-selected={contentTab === 'memories'}
          onClick={() => setContentTab('memories')}
          className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
            contentTab === 'memories'
              ? 'border-emerald-500 text-emerald-400'
              : 'border-transparent text-harbor-text-secondary hover:text-harbor-text'
          }`}
        >
          Memories
        </button>
        <button
          aria-selected={contentTab === 'prompts'}
          onClick={() => setContentTab('prompts')}
          className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
            contentTab === 'prompts'
              ? 'border-emerald-500 text-emerald-400'
              : 'border-transparent text-harbor-text-secondary hover:text-harbor-text'
          }`}
        >
          Prompts
        </button>
        <button
          aria-selected={contentTab === 'code'}
          onClick={() => setContentTab('code')}
          className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
            contentTab === 'code'
              ? 'border-emerald-500 text-emerald-400'
              : 'border-transparent text-harbor-text-secondary hover:text-harbor-text'
          }`}
        >
          Code Graph
        </button>
        <button
          aria-selected={contentTab === 'modules'}
          aria-label="Knowledge"
          onClick={() => setContentTab('modules')}
          className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
            contentTab === 'modules'
              ? 'border-emerald-500 text-emerald-400'
              : 'border-transparent text-harbor-text-secondary hover:text-harbor-text'
          }`}
        >
          Knowledge
          <span
            className="ml-2 rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-400"
            title="Experimental — behavior and data may change"
          >
            Experimental
          </span>
        </button>
      </div>

      {contentTab === 'connectors' ? (
        <div className="mt-6">
          <ConnectorList
            projectId={projectId}
            onAddConnector={() => setShowAddConnector(true)}
            onOpenDetail={(c: ConnectorResponse) => setDetailConnectorId(c.id)}
          />
        </div>
      ) : contentTab === 'memories' ? (
        <div className="mt-6 bg-harbor-surface border border-harbor-border rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <input
              type="text"
              value={memoriesSearchQuery}
              onChange={(e) => setMemoriesSearchQuery(e.target.value)}
              placeholder="Search memories..."
              className="flex-1 px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
            <span className="text-xs text-harbor-text-muted">{memories.length} memories</span>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-harbor-text">Memories</h2>
            <p className="text-sm text-harbor-text-secondary mt-1">
              Durable decisions, preferences and constraints agents have recorded for this project —
              separate from indexed documents.
            </p>
          </div>
          {canManage && (
            <MemoryComposer
              onSubmit={(text) => void handleAddMemory(text)}
              disabled={isLoadingMemories}
            />
          )}
          {isLoadingMemories && memories.length === 0 ? (
            <p className="text-sm text-harbor-text-secondary">Loading memories…</p>
          ) : (
            <MemoryList
              memories={pagedMemories}
              onDelete={(id) => void handleDeleteMemory(id)}
              onEdit={(id, text) => void handleEditMemory(id, text)}
            />
          )}
          {governanceLoaded && (v2Sessions.length > 0 || v2Summaries.length > 0 || v2Degraded) && (
            <div className="space-y-4 border-t border-harbor-border pt-4">
              <div>
                <h2 className="text-lg font-semibold text-harbor-text">Memory Engine v2</h2>
                <p className="text-sm text-harbor-text-secondary mt-1">
                  Sessions, handoffs, provenance, lifecycle, and index health from the v2 engine.
                </p>
              </div>
              <JobHealth degraded={v2Degraded} warnings={v2Warnings} pendingJobs={[]} />
              {v2Summaries.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-harbor-text mb-2">
                    Active v2 memories
                  </h3>
                  <ul className="space-y-2">
                    {v2Summaries.map((summary) => (
                      <li
                        key={summary.memoryId}
                        className="rounded-lg border border-harbor-border bg-harbor-surface p-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm text-harbor-text">
                              {summary.excerpt ?? summary.memoryId}
                            </p>
                            <p className="mt-1 text-xs text-harbor-text-muted">
                              {summary.lifecycle}
                              {summary.kind ? ` · ${summary.kind}` : ''} · v{summary.versionNumber}
                            </p>
                          </div>
                          <div className="flex shrink-0 gap-2">
                            <button
                              type="button"
                              onClick={() => void handleVerifyV2Memory(summary.memoryId)}
                              className="px-2 py-1 text-xs text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg"
                              aria-label={`Verify memory ${summary.memoryId}`}
                            >
                              Verify
                            </button>
                            {canManage &&
                              (summary.lifecycle === 'archived' ? (
                                <button
                                  type="button"
                                  onClick={() => void handleRestoreV2Memory(summary.memoryId)}
                                  className="px-2 py-1 text-xs text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg"
                                  aria-label={`Restore memory ${summary.memoryId}`}
                                >
                                  Restore
                                </button>
                              ) : (
                                summary.lifecycle === 'active' && (
                                  <button
                                    type="button"
                                    onClick={() => void handleArchiveV2Memory(summary.memoryId)}
                                    className="px-2 py-1 text-xs text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg"
                                    aria-label={`Archive memory ${summary.memoryId}`}
                                  >
                                    Archive
                                  </button>
                                )
                              ))}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {v2Verify && <ProvenanceChain verify={v2Verify} />}
              {v2Timeline.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-harbor-text mb-2">Timeline</h3>
                  <ul className="space-y-1 text-sm text-harbor-text-secondary">
                    {v2Timeline.map((item) => (
                      <li key={`${item.kind}-${item.id}`}>
                        <span className="text-xs text-harbor-text-muted">{item.kind}</span>{' '}
                        {item.summary}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <h3 className="text-sm font-semibold text-harbor-text mb-2">Sessions</h3>
                <SessionHistory
                  sessions={v2Sessions}
                  onSelect={(sessionId) => void handleSelectV2Session(sessionId)}
                />
              </div>
              {selectedSessionId &&
                v2Handoffs
                  .filter((handoff) => handoff.sessionId === selectedSessionId)
                  .map((handoff) => <HandoffDetails key={handoff.handoffId} handoff={handoff} />)}
            </div>
          )}
          {pagedMemories.length > 0 && (
            <div className="px-6 py-3 border-t border-harbor-border flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-harbor-text-muted">
                  Showing{' '}
                  {(memoriesPage - 1) * memoriesPageSize + 1 > displayedMemoriesWithSearch.length
                    ? displayedMemoriesWithSearch.length
                    : (memoriesPage - 1) * memoriesPageSize +
                      Math.min(
                        memoriesPageSize,
                        displayedMemoriesWithSearch.length - (memoriesPage - 1) * memoriesPageSize
                      )}{' '}
                  of {displayedMemoriesWithSearch.length}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setMemoriesPage((p) => Math.max(1, p - 1))}
                  disabled={memoriesPage === 1}
                  className="px-3 py-1.5 text-sm text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors"
                >
                  Prev
                </button>
                <span className="text-xs text-harbor-text-muted">
                  Page {memoriesPage} of{' '}
                  {Math.ceil(displayedMemoriesWithSearch.length / memoriesPageSize)}
                </span>
                <button
                  onClick={() =>
                    setMemoriesPage((p) =>
                      Math.min(
                        Math.ceil(displayedMemoriesWithSearch.length / memoriesPageSize),
                        p + 1
                      )
                    )
                  }
                  disabled={
                    memoriesPage >=
                      Math.ceil(displayedMemoriesWithSearch.length / memoriesPageSize) ||
                    displayedMemoriesWithSearch.length === 0
                  }
                  className="px-3 py-1.5 text-sm text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors"
                >
                  Next
                </button>
                <select
                  value={memoriesPageSize}
                  onChange={(e) => {
                    setMemoriesPageSize(Number(e.target.value))
                    setMemoriesPage(1)
                  }}
                  className="px-3 py-1.5 text-sm border border-harbor-border rounded-lg bg-harbor-surface text-harbor-text focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={75}>75</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          )}
        </div>
      ) : contentTab === 'prompts' ? (
        <section className="mt-6 overflow-hidden rounded-xl border border-harbor-border bg-harbor-surface">
          <div className="border-b border-harbor-border px-6 py-5">
            <div className="flex items-start gap-3">
              <div
                aria-hidden="true"
                className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-emerald-500/30 bg-emerald-500/10 font-mono text-sm text-emerald-400"
              >
                {'>_'}
              </div>
              <div>
                <h2 className="text-lg font-semibold text-harbor-text">Prompt library</h2>
                <p className="mt-1 text-sm text-harbor-text-secondary">
                  Inherited, local, and disabled prompts at this scope. Managers can inherit,
                  customize, disable, and preview the effective prompt.
                </p>
              </div>
            </div>
          </div>
          <ProjectPromptsTab projectId={projectId} canManage={canManage} />
        </section>
      ) : contentTab === 'code' ? (
        <div className="mt-6">
          <CodeGraphTab projectId={projectId} canManage={canManage} />
        </div>
      ) : contentTab === 'modules' ? (
        <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-start">
          <div className="lg:w-1/3 lg:min-w-[280px] rounded-xl border border-harbor-border bg-harbor-surface p-4">
            <ModulesTab
              projectId={projectId}
              canManage={canManage}
              selectedAreaId={selectedAreaId}
              onSelectArea={setSelectedAreaId}
              onAreasChanged={setKnowledgeAreas}
            />
          </div>
          <div className="flex-1">
            <AreaDrilldownPanel
              area={knowledgeAreas.find((a) => a.id === selectedAreaId) ?? null}
              areas={knowledgeAreas}
              projectId={projectId}
              canManage={canManage}
            />
          </div>
        </div>
      ) : (
        /* Files Table */
        <div className="bg-harbor-surface border border-harbor-border rounded-xl">
          <div className="p-6 border-b border-harbor-border flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-harbor-text">Files</h2>
              <p className="text-sm text-harbor-text-secondary mt-1">
                {canManage
                  ? 'Download originals, replace a document to update content, or delete to remove chunks and files.'
                  : 'Download and preview indexed documents.'}
              </p>
              {canManage && (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="text"
                    value={filesSearchQuery}
                    onChange={(e) => setFilesSearchQuery(e.target.value)}
                    placeholder="Search files..."
                    className="flex-1 px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                  <span className="text-xs text-harbor-text-muted">{files.length} files</span>
                </div>
              )}
            </div>
            {canManage && files.length > 0 && (
              <label className="flex items-center gap-2 text-sm text-harbor-text-secondary cursor-pointer flex-shrink-0 select-none">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={allSelectableSelected}
                  onChange={handleSelectAll}
                  disabled={selectableFiles.length === 0}
                  className="h-4 w-4 rounded border-harbor-border bg-harbor-surface-raised text-emerald-600 focus:ring-emerald-500 focus:ring-offset-harbor-ring-offset disabled:opacity-40"
                />
                Select all
              </label>
            )}
          </div>

          {(fileDocTypes.length >= 2 || files.some((f) => f.indexingStatus === 'failed')) && (
            <div className="px-6 py-3 border-b border-harbor-border flex flex-wrap items-center gap-2">
              {fileDocTypes.length >= 2 && (
                <>
                  <span className="text-xs text-harbor-text-muted shrink-0">Document type</span>
                  <DocTypeChipFilter
                    value={filesDocTypeFilter}
                    onChange={setFilesDocTypeFilter}
                    types={fileDocTypes}
                    showAll
                  />
                </>
              )}
              {files.some((f) => f.indexingStatus === 'failed') && (
                <button
                  type="button"
                  onClick={() => setFilesShowFailedOnly((prev) => !prev)}
                  aria-pressed={filesShowFailedOnly}
                  className={`ml-auto px-2.5 py-0.5 text-xs font-medium rounded-full border transition-colors ${
                    filesShowFailedOnly
                      ? 'bg-red-500/20 border-red-500 text-red-300'
                      : 'bg-harbor-surface-raised border-harbor-border text-harbor-text-secondary hover:border-gray-500'
                  }`}
                >
                  Indexing Failed
                </button>
              )}
              {(filesDocTypeFilter.length > 0 || filesShowFailedOnly) && (
                <span className="text-xs text-harbor-text-muted">
                  {displayedFiles.length} of {files.length}
                </span>
              )}
            </div>
          )}

          {canManage && selectedFileIds.size > 0 && (
            <div className="px-6 py-3 border-b border-harbor-border bg-emerald-500/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-emerald-200">
                {selectedFileIds.size} file{selectedFileIds.size === 1 ? '' : 's'} selected
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedPending.length > 0 && (
                  <Button size="sm" onClick={handleIndexSelected} disabled={isIndexing}>
                    Index selected ({selectedPending.length})
                  </Button>
                )}
                {selectedIndexed.length > 0 && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleReindexSelected}
                    disabled={isIndexing}
                  >
                    Reindex selected ({selectedIndexed.length})
                  </Button>
                )}
                {selectedRetryable.length > 0 && (
                  <Button
                    size="sm"
                    onClick={handleRetrySelected}
                    disabled={isIndexing}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Retry selected ({selectedRetryable.length})
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={openBulkDeleteModal}
                  disabled={isIndexing || isDeleting}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete selected ({selectedFileIds.size})
                </Button>
                <Button size="sm" variant="ghost" onClick={clearSelection}>
                  Clear selection
                </Button>
              </div>
            </div>
          )}

          {files.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-5xl mb-4">📄</div>
              <h3 className="text-lg font-semibold text-harbor-text mb-2">No files uploaded</h3>
              <p className="text-sm text-harbor-text-secondary mb-4">
                {canManage ? 'Upload documents to start indexing' : 'No files in this project yet'}
              </p>
              {canManage && (
                <Link
                  to={`/projects/${projectId}/upload`}
                  className="inline-flex items-center px-4 py-2 bg-emerald-600 text-harbor-text text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  Upload Files
                </Link>
              )}
            </div>
          ) : pagedFiles.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-sm text-harbor-text-secondary">
                {filesShowFailedOnly
                  ? 'No failed files match the current filters.'
                  : 'No files match the selected document types.'}
              </p>
              <button
                type="button"
                onClick={() => {
                  setFilesDocTypeFilter([])
                  setFilesShowFailedOnly(false)
                }}
                className="mt-3 text-sm text-emerald-400 hover:text-emerald-300 transition-colors"
              >
                Show all
              </button>
            </div>
          ) : (
            <div className="divide-y divide-harbor-border">
              {pagedFiles.map((file) => {
                const selectable = isFileSelectable(file, isIndexing)
                return (
                  <div
                    key={file.id}
                    className={`flex items-center justify-between p-4 hover:bg-harbor-surface-raised/50 transition-colors gap-4 ${
                      selectedFileIds.has(file.id) ? 'bg-emerald-500/5' : ''
                    }`}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      {canManage && (
                        <input
                          type="checkbox"
                          checked={selectedFileIds.has(file.id)}
                          onChange={() => toggleFileSelection(file.id)}
                          disabled={!selectable}
                          aria-label={`Select ${file.originalFilename}`}
                          className="h-4 w-4 rounded border-harbor-border bg-harbor-surface-raised text-emerald-600 focus:ring-emerald-500 focus:ring-offset-harbor-ring-offset disabled:opacity-40 flex-shrink-0"
                        />
                      )}
                      <div className="w-10 h-10 rounded-lg bg-harbor-surface-raised flex items-center justify-center flex-shrink-0">
                        <FileTypeIcon fileType={file.fileType} className="w-8 h-8" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-harbor-text truncate">
                          {file.originalFilename}
                        </p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-harbor-text-secondary">
                          <span>{file.fileType.toUpperCase()}</span>
                          <span>·</span>
                          <span>{formatFileSize(file.fileSize)}</span>
                          {file.chunkCount > 0 && (
                            <>
                              <span>·</span>
                              <span>{file.chunkCount} chunks</span>
                            </>
                          )}
                          <span>·</span>
                          <span
                            className="text-harbor-text-muted"
                            title={file.createdAt ?? undefined}
                          >
                            {formatTimestamp(file.createdAt)}
                          </span>
                        </div>
                        {file.indexingStatus === 'failed' && file.errorMessage && (
                          <p className="text-xs text-red-400 mt-1 truncate">{file.errorMessage}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {file.indexingStatus === 'indexing' ? (
                        <IndexingStepLine
                          phase={file.indexingPhase ?? 'parsing'}
                          includeEnrich={Boolean(indexingJob?.enrichmentEnabled)}
                        />
                      ) : (
                        <>
                          <StatusBadge status={file.indexingStatus} />
                          <EnrichmentStatusTag status={file.enrichmentStatus ?? null} />
                        </>
                      )}
                      <button
                        onClick={() => setPreviewFile(file)}
                        className="p-2 text-harbor-text-muted hover:text-sky-400 rounded-lg hover:bg-harbor-surface-raised transition-colors"
                        title="View markdown preview"
                        aria-label={`View ${file.originalFilename}`}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDownload(file)}
                        className="p-2 text-harbor-text-muted hover:text-emerald-400 rounded-lg hover:bg-harbor-surface-raised transition-colors"
                        title="Download original"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                          />
                        </svg>
                      </button>
                      {canManage && file.indexingStatus !== 'indexing' && (
                        <button
                          onClick={() => openReplacePicker(file)}
                          disabled={replacingFileId === file.id}
                          className="p-2 text-harbor-text-muted hover:text-amber-400 rounded-lg hover:bg-harbor-surface-raised transition-colors disabled:opacity-50"
                          title="Replace document (clears old chunks, re-index after)"
                          aria-label={`Replace ${file.originalFilename}`}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 4v6h6M20 20v-6h-6M5 19a9 9 0 0114-7.5M19 5A9 9 0 015 12.5"
                            />
                          </svg>
                        </button>
                      )}
                      {canManage && file.indexingStatus === 'pending' && (
                        <button
                          onClick={() => handleIndex([file.id])}
                          disabled={isIndexing}
                          className="px-2 py-1 text-xs text-emerald-400 hover:text-emerald-300 hover:bg-harbor-surface-raised rounded transition-colors disabled:opacity-50"
                        >
                          Index
                        </button>
                      )}
                      {canManage && file.indexingStatus === 'failed' && (
                        <button
                          onClick={() => handleRetryFailed([file.id])}
                          disabled={isIndexing}
                          className="px-2 py-1 text-xs text-red-400 hover:text-red-300 hover:bg-harbor-surface-raised rounded transition-colors disabled:opacity-50"
                        >
                          Retry
                        </button>
                      )}
                      {canManage && file.indexingStatus === 'indexing' && !isIndexing && (
                        <button
                          onClick={() => handleRetryStuck([file.id])}
                          disabled={isIndexing}
                          className="px-2 py-1 text-xs text-amber-400 hover:text-amber-300 hover:bg-harbor-surface-raised rounded transition-colors disabled:opacity-50"
                        >
                          Retry
                        </button>
                      )}
                      {canManage && file.indexingStatus === 'indexed' && (
                        <button
                          onClick={() => handleReindexFile(file.id)}
                          disabled={isIndexing}
                          className="px-2 py-1 text-xs text-amber-400 hover:text-amber-300 hover:bg-harbor-surface-raised rounded transition-colors disabled:opacity-50"
                        >
                          Reindex
                        </button>
                      )}
                      {/* Distillation badge + button (transcript files only) */}
                      {file.docType === 'transcript' && file.distillationState === 'proposed' && (
                        <button
                          onClick={() => setDistillFileId(file.id)}
                          className="px-2 py-1 text-xs text-amber-400 hover:text-amber-300 hover:bg-harbor-surface-raised rounded transition-colors"
                        >
                          Distill: proposed
                        </button>
                      )}
                      {file.docType === 'transcript' && file.distillationState === 'applied' && (
                        <span className="px-2 py-1 text-xs text-emerald-400 bg-emerald-900/20 rounded">
                          Distilled
                        </span>
                      )}
                      {file.docType === 'transcript' && distillingFileId === file.id && (
                        <span className="px-2 py-1 text-xs text-purple-400 bg-harbor-surface-raised rounded">
                          Distilling…
                        </span>
                      )}
                      {canManage &&
                        file.docType === 'transcript' &&
                        file.indexingStatus === 'indexed' &&
                        !file.distillationState &&
                        !distillingFileId &&
                        (() => {
                          const hasLlm = Boolean(
                            accountSettings?.llmBaseUrl && accountSettings?.llmModelName
                          )
                          return (
                            <button
                              onClick={async () => {
                                if (!hasLlm) return
                                setDistillingFileId(file.id)
                                try {
                                  const { jobId } = await distillFile(file.id)
                                  toast.success('Distillation started')
                                  setDistillJobId(jobId)
                                } catch (err) {
                                  toast.error(err instanceof Error ? err.message : 'Distill failed')
                                  setDistillingFileId(null)
                                }
                              }}
                              disabled={!hasLlm}
                              title={
                                hasLlm
                                  ? 'Distill this transcript'
                                  : 'Configure an LLM provider to distill transcripts'
                              }
                              className={`px-2 py-1 text-xs rounded transition-colors ${
                                hasLlm
                                  ? 'text-purple-400 hover:text-purple-300 hover:bg-harbor-surface-raised'
                                  : 'text-harbor-text-muted opacity-50 cursor-not-allowed'
                              }`}
                            >
                              Distill
                            </button>
                          )
                        })()}
                      {canManage && (
                        <button
                          onClick={() => openDeleteModal(file.id, file.originalFilename)}
                          className="p-2 text-harbor-text-muted hover:text-red-400 rounded-lg hover:bg-harbor-surface-raised transition-colors"
                          title="Delete file"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          {displayedFilesWithSearch.length > 0 && (
            <div className="px-6 py-3 border-t border-harbor-border flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-harbor-text-muted">
                  Showing{' '}
                  {(filesPage - 1) * filesPageSize + 1 > displayedFilesWithSearch.length
                    ? displayedFilesWithSearch.length
                    : (filesPage - 1) * filesPageSize +
                      Math.min(
                        filesPageSize,
                        displayedFilesWithSearch.length - (filesPage - 1) * filesPageSize
                      )}{' '}
                  of {displayedFilesWithSearch.length}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setFilesPage((p) => Math.max(1, p - 1))}
                  disabled={filesPage === 1}
                  className="px-3 py-1.5 text-sm text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors"
                >
                  Prev
                </button>
                <span className="text-xs text-harbor-text-muted">
                  Page {filesPage} of {Math.ceil(displayedFilesWithSearch.length / filesPageSize)}
                </span>
                <button
                  onClick={() =>
                    setFilesPage((p) =>
                      Math.min(Math.ceil(displayedFilesWithSearch.length / filesPageSize), p + 1)
                    )
                  }
                  disabled={
                    filesPage >= Math.ceil(displayedFilesWithSearch.length / filesPageSize) ||
                    displayedFilesWithSearch.length === 0
                  }
                  className="px-3 py-1.5 text-sm text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors"
                >
                  Next
                </button>
                <select
                  value={filesPageSize}
                  onChange={(e) => {
                    setFilesPageSize(Number(e.target.value))
                    setFilesPage(1)
                  }}
                  className="px-3 py-1.5 text-sm border border-harbor-border rounded-lg bg-harbor-surface text-harbor-text focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={75}>75</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Connector modals */}
      <AddConnectorModal
        isOpen={showAddConnector}
        projectId={projectId}
        onClose={() => setShowAddConnector(false)}
        onCreated={() => {
          setShowAddConnector(false)
        }}
      />

      {detailConnectorId && (
        <ConnectorDetail
          connectorId={detailConnectorId}
          onClose={() => setDetailConnectorId(null)}
        />
      )}

      <Modal
        isOpen={stopConfirmOpen}
        onClose={() => setStopConfirmOpen(false)}
        title="Stop indexing?"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-harbor-text-secondary">
            In-progress enrichment will abort and unfinished files will return to pending.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setStopConfirmOpen(false)}
              disabled={isStopping}
              className="px-4 py-2 text-sm text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors"
            >
              Cancel
            </button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={() => void executeStopIndexing()}
              disabled={isStopping}
            >
              {isStopping ? 'Stopping…' : 'Stop indexing'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={reindexAllModal}
        onClose={() => setReindexAllModal(false)}
        title="Reindex all files?"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-harbor-text-secondary">
            This will clear existing search chunks and re-process every file in this project.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setReindexAllModal(false)}
              className="px-4 py-2 text-sm text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors"
            >
              Cancel
            </button>
            <Button onClick={handleReindexAll} disabled={isIndexing}>
              Reindex all
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={embeddingModal}
        onClose={() => {
          if (!isSavingEmbedding && !isSavingDescription) {
            setEmbeddingModal(false)
            setEmbeddingConfirm(false)
          }
        }}
        title={embeddingConfirm ? 'Confirm embedding change' : 'Project settings'}
        size="lg"
      >
        <div className="space-y-4">
          {!embeddingConfirm ? (
            <>
              <div>
                <label
                  htmlFor="proj-name"
                  className="block text-sm font-medium text-harbor-text-secondary mb-1"
                >
                  Name
                </label>
                <input
                  id="proj-name"
                  type="text"
                  value={project.name}
                  readOnly
                  className={`${inputClassName} text-harbor-text-secondary cursor-not-allowed`}
                />
                <p className="text-xs text-harbor-text-muted mt-1">
                  Project name cannot be changed (used as the search / MCP key).
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
                  Icon
                </label>
                <div className="flex items-center gap-4">
                  {(() => {
                    const authIconUrl = getAuthenticatedIconUrl(project.iconUrl)
                    return authIconUrl && failedIconUrl !== authIconUrl ? (
                      <img
                        src={authIconUrl}
                        alt=""
                        className="w-16 h-16 rounded-lg object-cover"
                        onError={() => setFailedIconUrl(authIconUrl)}
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-emerald-600/20 text-emerald-400 flex items-center justify-center text-2xl font-bold">
                        {project.name[0]?.toUpperCase() ?? '?'}
                      </div>
                    )
                  })()}
                  <div className="flex flex-col gap-2">
                    <input
                      ref={iconInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                      className="hidden"
                      onChange={(e) => void handleIconUpload(e)}
                    />
                    <button
                      type="button"
                      onClick={() => iconInputRef.current?.click()}
                      disabled={isUploadingIcon}
                      className="px-3 py-1.5 text-sm bg-harbor-surface-raised border border-harbor-border rounded-lg hover:bg-harbor-surface transition-colors disabled:opacity-50"
                    >
                      {isUploadingIcon
                        ? 'Uploading…'
                        : project.iconUrl
                          ? 'Change icon'
                          : 'Upload icon'}
                    </button>
                    {project.iconUrl && (
                      <button
                        type="button"
                        onClick={() => void handleIconDelete()}
                        disabled={isUploadingIcon}
                        className="px-3 py-1.5 text-sm text-red-400 hover:text-red-300 hover:bg-harbor-surface-raised rounded-lg transition-colors disabled:opacity-50"
                      >
                        Remove icon
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-harbor-text-muted mt-1">
                  Recommended: 128 × 128 px. Images are resized to fit.
                </p>
              </div>
              <div>
                <label
                  htmlFor="proj-description"
                  className="block text-sm font-medium text-harbor-text-secondary mb-1"
                >
                  Description
                </label>
                <textarea
                  id="proj-description"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                  maxLength={1024}
                  className={inputClassName}
                  placeholder="Optional description"
                />
                <div className="flex justify-end mt-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => void handleSaveDescription()}
                    disabled={
                      isSavingDescription ||
                      (editDescription.trim() || '') === (project.description ?? '')
                    }
                  >
                    {isSavingDescription ? 'Saving…' : 'Save description'}
                  </Button>
                </div>
              </div>

              {canManage && (
                <div className="border-t border-gray-800 pt-4">
                  <label className="flex items-center justify-between gap-4 cursor-pointer">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">Contextual enrichment</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Queue background LLM enrichment after indexing. LLM endpoint is configured
                        in{' '}
                        <Link
                          to="/admin/settings"
                          className="text-emerald-400 hover:text-emerald-300"
                        >
                          Admin Settings
                        </Link>
                        . Does not reindex existing files when toggled.
                      </p>
                      {accountSettings && !accountSettings.contextualEnrichmentEnabled && (
                        <p className="text-xs text-amber-400/80 mt-2">
                          Global enrichment is disabled in System Settings — enable it there first
                          to use per-project enrichment.
                        </p>
                      )}
                      {editContextualEnrichment &&
                        accountSettings?.contextualEnrichmentEnabled &&
                        !(accountSettings.llmBaseUrl && accountSettings.llmModelName) && (
                          <p className="text-xs text-amber-400/80 mt-2">
                            LLM settings are incomplete — enrichment will stay off until an admin
                            finishes setup.
                          </p>
                        )}
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={editContextualEnrichment}
                      aria-disabled={
                        accountSettings ? !accountSettings.contextualEnrichmentEnabled : false
                      }
                      disabled={Boolean(
                        accountSettings && !accountSettings.contextualEnrichmentEnabled
                      )}
                      onClick={() => {
                        if (accountSettings && !accountSettings.contextualEnrichmentEnabled) return
                        setEditContextualEnrichment((v) => !v)
                      }}
                      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed ${
                        editContextualEnrichment ? 'bg-emerald-600' : 'bg-gray-700'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          editContextualEnrichment ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </label>
                  <div className="flex justify-end mt-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => void handleSaveEnrichmentToggle()}
                      disabled={
                        isSavingEnrichmentToggle ||
                        (accountSettings ? !accountSettings.contextualEnrichmentEnabled : false) ||
                        editContextualEnrichment === (project.contextualEnrichmentEnabled ?? false)
                      }
                    >
                      {isSavingEnrichmentToggle ? 'Saving…' : 'Save enrichment setting'}
                    </Button>
                  </div>
                </div>
              )}

              {canManage && (
                <div className="border-t border-gray-800 pt-4">
                  <label className="flex items-center justify-between gap-4 cursor-pointer">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">Transcript distillation</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Automatically condense transcript segments with LLM after indexing. Drops
                        filler, extracts decisions and action items.
                      </p>
                      {accountSettings && !accountSettings.transcriptDistillationEnabled && (
                        <p className="text-xs text-amber-400/80 mt-2">
                          Global transcript distillation is disabled in System Settings — enable it
                          there first.
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={editTranscriptDistillation}
                      aria-disabled={
                        accountSettings ? !accountSettings.transcriptDistillationEnabled : false
                      }
                      disabled={Boolean(
                        accountSettings && !accountSettings.transcriptDistillationEnabled
                      )}
                      onClick={() => {
                        if (accountSettings && !accountSettings.transcriptDistillationEnabled)
                          return
                        setEditTranscriptDistillation((v) => !v)
                      }}
                      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed ${
                        editTranscriptDistillation ? 'bg-emerald-600' : 'bg-gray-700'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          editTranscriptDistillation ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </label>
                  <div className="flex justify-end mt-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={async () => {
                        if (!projectId) return
                        setIsSavingDistillationToggle(true)
                        try {
                          await updateProject(projectId, {
                            transcriptDistillationEnabled: editTranscriptDistillation,
                          })
                          toast.success('Distillation setting saved')
                          void fetchData()
                        } catch (err) {
                          toast.error(err instanceof Error ? err.message : 'Save failed')
                        } finally {
                          setIsSavingDistillationToggle(false)
                        }
                      }}
                      disabled={
                        isSavingDistillationToggle ||
                        (accountSettings
                          ? !accountSettings.transcriptDistillationEnabled
                          : false) ||
                        editTranscriptDistillation ===
                          (project.transcriptDistillationEnabled ?? false)
                      }
                    >
                      {isSavingDistillationToggle ? 'Saving…' : 'Save distillation setting'}
                    </Button>
                  </div>
                </div>
              )}

              {project.embedding && globalAdmin && (
                <>
                  <div className="border-t border-harbor-border pt-4">
                    <p className="text-sm font-medium text-harbor-text mb-1">Embedding</p>
                    <p className="text-sm text-harbor-text-secondary mb-3">
                      The embedding model is locked forever once files are indexed. You can only
                      move this project to a different{' '}
                      <Link
                        to="/admin/settings"
                        className="text-emerald-400 hover:text-emerald-300"
                      >
                        system embedding provider
                      </Link>{' '}
                      that serves the exact same model (matching dimensions and pipeline) — useful
                      for rotating credentials or moving to a faster host.
                    </p>
                    <div className="space-y-4">
                      <div>
                        <label
                          htmlFor="proj-provider"
                          className="block text-sm font-medium text-harbor-text-secondary mb-1"
                        >
                          Embedding provider
                        </label>
                        <select
                          id="proj-provider"
                          value={selectedEmbeddingProviderId}
                          onChange={(e) => setSelectedEmbeddingProviderId(Number(e.target.value))}
                          className={inputClassName}
                          disabled={isIndexing}
                        >
                          <option value="">Select a provider…</option>
                          {systemEmbeddingProviders.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.label} — {p.provider} ({p.modelName ?? 'no model'})
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-harbor-text-muted">
                          Current: {providerLabel(project.embedding.provider)} ·{' '}
                          {project.embedding.modelName}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      onClick={() => setEmbeddingModal(false)}
                      className="px-4 py-2 text-sm text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors"
                    >
                      Close
                    </button>
                    <Button
                      onClick={() => setEmbeddingConfirm(true)}
                      disabled={isIndexing || selectedEmbeddingProviderId === ''}
                      title={isIndexing ? 'Disabled while indexing' : undefined}
                    >
                      Save embedding provider…
                    </Button>
                  </div>
                </>
              )}

              {!project.embedding && (
                <div className="flex justify-end pt-2">
                  <button
                    onClick={() => setEmbeddingModal(false)}
                    className="px-4 py-2 text-sm text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              )}
            </>
          ) : project.embedding ? (
            <>
              <div className="rounded-lg border border-harbor-border bg-harbor-surface/60 p-3 text-sm space-y-2">
                <p className="text-harbor-text-secondary">Current</p>
                <p className="text-harbor-text">
                  {providerLabel(project.embedding.provider)} · {project.embedding.modelName}
                  {project.embedding.apiBaseUrl ? ` · ${project.embedding.apiBaseUrl}` : ''}
                </p>
                <p className="text-harbor-text-secondary pt-2">New provider</p>
                <p className="text-harbor-text">
                  {systemEmbeddingProviders.find((p) => p.id === selectedEmbeddingProviderId)
                    ?.label ?? '—'}
                </p>
              </div>
              <p className="text-harbor-text-secondary">
                The server verifies this provider's model produces identical vector dimensions
                before applying the change — no reindex is needed or triggered.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setEmbeddingConfirm(false)}
                  disabled={isSavingEmbedding}
                  className="px-4 py-2 text-sm text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors"
                >
                  Back
                </button>
                <Button onClick={() => void handleSaveEmbedding()} disabled={isSavingEmbedding}>
                  {isSavingEmbedding ? 'Saving…' : 'Confirm change'}
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </Modal>

      <Modal
        isOpen={reindexModal.isOpen}
        onClose={() => setReindexModal({ isOpen: false, fileId: null, fileName: '' })}
        title="Re-index document?"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-harbor-text-secondary">
            <span className="font-semibold text-harbor-text">{reindexModal.fileName}</span>{' '}
            {reindexModal.afterReplace
              ? 'was replaced. Old search chunks were cleared. Start indexing now to make the new content searchable?'
              : 'will be reindexed. Existing search chunks will be replaced.'}
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setReindexModal({ isOpen: false, fileId: null, fileName: '' })}
              className="px-4 py-2 text-sm text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors"
            >
              Later
            </button>
            <Button onClick={handleReindexAfterReplace} disabled={isIndexing}>
              Re-index now
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        title={deleteModal.mode === 'bulk' ? 'Delete selected files?' : 'Delete File'}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-harbor-text-secondary">
            {deleteModal.mode === 'bulk' ? (
              <>
                Delete{' '}
                <span className="font-semibold text-harbor-text">
                  {deleteModal.count} file{deleteModal.count === 1 ? '' : 's'}
                </span>
                ? This removes the stored originals and all indexed vectors in LanceDB for the
                selection. You can upload again afterward.
              </>
            ) : (
              <>
                Are you sure you want to delete{' '}
                <span className="font-semibold text-harbor-text">{deleteModal.fileName}</span>? This
                removes the stored original and all indexed vectors for this document.
              </>
            )}
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={closeDeleteModal}
              disabled={isDeleting}
              className="px-4 py-2 text-sm text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteFile}
              disabled={isDeleting}
              className="px-4 py-2 bg-red-600 text-harbor-text text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {isDeleting
                ? 'Deleting...'
                : deleteModal.mode === 'bulk'
                  ? `Delete ${deleteModal.count}`
                  : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={cleanupPreview !== null}
        onClose={() => {
          if (!isCleaning) {
            setCleanupPreview(null)
            setSelectedCleanupOptions(new Set())
          }
        }}
        title="Clean up project"
        size="md"
      >
        {cleanupPreview && (
          <div className="space-y-4">
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4">
              <p className="font-medium text-amber-200">
                Selected cleanup actions permanently remove data and cannot be undone.
              </p>
              <p className="mt-2 text-sm text-amber-100/80">
                Choose what to reclaim. Old file versions keep the newest upload per filename.
              </p>
            </div>
            <ul className="max-h-72 space-y-2 overflow-y-auto">
              {cleanupPreview.options.map((option) => {
                const disabled = option.itemCount === 0
                const checked = selectedCleanupOptions.has(option.option)
                return (
                  <li
                    key={option.option}
                    className="rounded-lg border border-harbor-border bg-harbor-surface-raised/40 p-3"
                  >
                    <label
                      className={`flex gap-3 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={checked}
                        disabled={disabled || isCleaning}
                        onChange={() => toggleCleanupOption(option.option)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-baseline justify-between gap-2">
                          <span className="font-medium text-harbor-text">{option.label}</span>
                          <span className="shrink-0 text-xs text-harbor-text-secondary">
                            {option.itemCount} item{option.itemCount === 1 ? '' : 's'}
                            {option.approximateBytes !== '0'
                              ? ` · ~${formatCleanupBytes(option.approximateBytes)}`
                              : ''}
                          </span>
                        </span>
                        <span className="mt-1 block text-sm text-harbor-text-secondary">
                          {option.description}
                        </span>
                        {option.option === 'oldFileVersions' &&
                          option.groups &&
                          option.groups.length > 0 &&
                          checked && (
                            <ul className="mt-2 max-h-28 space-y-1 overflow-y-auto rounded border border-harbor-border/60 bg-harbor-surface/50 p-2 text-xs text-harbor-text-secondary">
                              {option.groups.map((group) => (
                                <li key={group.filename} className="flex justify-between gap-3">
                                  <span className="truncate text-harbor-text">
                                    {group.filename}
                                  </span>
                                  <span className="shrink-0">{group.deleteFileIds.length} old</span>
                                </li>
                              ))}
                            </ul>
                          )}
                      </span>
                    </label>
                  </li>
                )
              })}
            </ul>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setCleanupPreview(null)
                  setSelectedCleanupOptions(new Set())
                }}
                disabled={isCleaning}
                className="px-4 py-2 text-sm text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleRunCleanup()}
                disabled={isCleaning || selectedCleanupOptions.size === 0}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isCleaning ? 'Cleaning…' : `Clean up (${selectedCleanupOptions.size} selected)`}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <MarkdownPreviewModal
        file={previewFile}
        isOpen={previewFile !== null}
        onClose={() => setPreviewFile(null)}
      />

      <TranscriptDistillModal
        fileId={distillFileId ?? 0}
        isOpen={distillFileId !== null}
        onClose={() => setDistillFileId(null)}
        onAccepted={() => {
          void fetchData()
        }}
        onRejected={() => {
          void fetchData()
        }}
      />
    </div>
  )
}
